'use client';

import { useEffect, useState } from 'react';
import axios from 'axios';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { BuletinItem } from '@/types/buletin';
import { ApiResponse } from '@/types/api';
import { useTranslation } from '@/context/LanguageContext';
import SearchBar from '@/components/ui/SearchBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faChevronRight, faChevronLeft, faBookOpen, faDownload,
    faCalendar, faArrowRight
} from '@fortawesome/free-solid-svg-icons';

import { useUnit } from '@/context/UnitContext';

// ...

export default function BuletinContent() {
    const { unitId } = useUnit();
    const { language } = useTranslation();
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
    const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
    const searchParams = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const [buletinData, setBuletinData] = useState<BuletinItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0); // 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const [isMounted, setIsMounted] = useState(false);
    const limit = 12;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        // Reset to first page when search query changes
        setCurrentPage(0);
    }, [searchQuery]);

    useEffect(() => {
        const fetchBuletin = async () => {
            if (!unitId) return;

            setLoading(true);
            try {
                const keywordParam = searchQuery ? `&keyword=${encodeURIComponent(searchQuery)}` : '';
                const response = await axios.post<ApiResponse<BuletinItem[]>>('/api/doServices', {
                    method: 'GET',
                    endpoint: `web/newsletter?limit=${limit}&page=${currentPage + 1}&unit_id=${unitId}&order=CREATED_AT DESC${keywordParam}`
                });

                const apiData = response.data;
                if (apiData && apiData.data) {
                    setBuletinData(apiData.data);
                    setTotalPages(Number(apiData.metadata?.total_page) || 0);
                } else {
                    setBuletinData([]);
                    setTotalPages(0);
                }
            } catch (error: any) {
                console.error('❌ Error fetching buletin:', error.response?.data || error.message);
                setBuletinData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchBuletin();
    }, [currentPage, searchQuery, unitId]);

    // Pagination helpers
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible);

        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible);
        }

        if (currentPage > 0) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            );
        }

        for (let i = start; i < end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${i === currentPage
                        ? 'bg-unmulGreen text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    {i + 1}
                </button>
            );
        }

        if (end < totalPages) {
            pages.push(
                <span key="ellipsis" className="w-10 h-10 flex items-center justify-center text-gray-400">
                    ...
                </span>
            );
            pages.push(
                <button
                    key={totalPages - 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    {totalPages}
                </button>
            );
        }

        if (currentPage < totalPages - 1) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            );
        }

        return pages;
    };

    const getCoverUrl = (item: BuletinItem) => {
        if (item.imagename) {
            return `https://unmul.ac.id/uploads/magazines/covers/${item.imagename}`;
        }
        return `/api/doServicesProxy?endpoint=web/file/newsletter_cover/${item.id}&isImage=true`;
    };

    const getFileUrl = (item: BuletinItem) => {
        if (item.filename) {
            return `https://unmul.ac.id/uploads/magazines/${item.filename}`;
        }
        return `/api/doServicesProxy?endpoint=web/file/image/newsletter_file/${item.id}`;
    };

    return (
        <>
            {/* Hero Header - Creative Theme Colored */}
            <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
                {/* SVG Ornaments Background */}
                <div className="absolute inset-0 opacity-10">
                    <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                    </svg>
                    {/* Dots Pattern */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[url('/img/pattern-dots.svg')] opacity-20 transform rotate-45"></div>
                    {/* Circular Blurs */}
                    <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
                        <Link href="/" className="hover:text-white transition"><FontAwesomeIcon icon={faHome} /></Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-unmulYellow font-medium">Buletin</span>
                    </nav>

                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" data-aos="fade-up">
                        {language === 'en' ? 'Digital' : 'Buletin'} <span className="text-unmulYellow">{language === 'en' ? 'Bulletin' : 'Digital'}</span>
                    </h1>
                    <p className="text-white/80 text-lg max-w-2xl" data-aos="fade-up" data-aos-delay="100">
                        {language === 'en' 
                          ? `Read and download the latest editions of bulletins and official magazines from ${siteNameEnglish}`
                          : `Baca dan unduh edisi terbaru buletin dan majalah resmi ${siteName}`
                        }
                    </p>
                </div>
            </section>

            {/* Buletin Grid */}
            <section className="py-12 bg-unmulPale">
                <div className="container mx-auto px-6">
                    <SearchBar placeholder="Cari buletin..." className="mb-10" />

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
                        </div>
                    ) : buletinData.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <FontAwesomeIcon icon={faBookOpen} className="text-4xl text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg">
                                {searchQuery ? `Tidak ada buletin untuk keyword "${searchQuery}"` : 'Belum ada buletin yang tersedia.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                                {buletinData.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className="bg-white rounded-xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col group"
                                        data-aos={isMounted ? "fade-up" : undefined}
                                        data-aos-delay={isMounted ? index * 50 : undefined}
                                    >
                                        {/* Cover Image */}
                                        <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
                                            <img
                                                src={getCoverUrl(item)}
                                                alt={language === 'en' ? item.title_en : item.title_idn}
                                                className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                                loading="lazy"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    if (item.id && !target.src.includes('doServicesProxy') && !target.src.includes('placeholder')) {
                                                        target.src = `/api/doServicesProxy?endpoint=web/file/newsletter_cover/${item.id}&isImage=true`;
                                                    } else {
                                                        target.src = 'https://via.placeholder.com/300x400/eee/999?text=Cover';
                                                    }
                                                }}
                                            />
                                            {/* Hover Overlay with Download Button */}
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                                                <a
                                                    href={getFileUrl(item)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-6 py-2 bg-unmulYellow text-unmulGreen font-bold rounded-full transform translate-y-4 group-hover:translate-y-0 transition duration-300 hover:bg-white hover:text-unmulGreen flex items-center gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faDownload} />
                                                    Unduh PDF
                                                </a>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="p-5 flex-1 flex flex-col">
                                            <div className="text-xs text-gray-400 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {new Date(item.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </div>
                                            <h3 className="font-serif font-bold text-lg text-gray-900 group-hover:text-unmulGreen transition line-clamp-2 mb-3 leading-tight">
                                                {language === 'en' ? (item.title_en || item.title_idn) : (item.title_idn || item.title_en)}
                                            </h3>

                                            <div className="mt-auto pt-3 border-t border-gray-100">
                                                <a
                                                    href={getFileUrl(item)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm font-semibold text-unmulGreen hover:underline inline-flex items-center gap-1"
                                                >
                                                    Baca Sekarang
                                                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="mt-12 flex justify-center items-center gap-2">
                                {renderPagination()}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
